// Login — ports app/login/page.tsx + FormularioLogin.tsx.
//
// Real authentication: Better Auth, email + password, bearer token. There is
// no sign-up link and no "forgot password" — §8 makes this a closed space with
// two seeded accounts, and recovery is a script one of us runs.

import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { couple } from "@historia/shared/content";
import { safeDestination } from "@historia/shared/validation";
import { Flourish } from "@/components/ui/Flourish";
import { useTitle } from "@/components/ui/useTitle";
import { ApiError, signIn } from "@/api/client";

export function Login() {
  useTitle("Entrar");

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Rejects absolute URLs and "//host", so `?seguinte=` can't bounce anyone
  // off-site. Same guard the old Server Action applied, now shared so the API
  // and the web app can't disagree about it.
  const destino = safeDestination(params.get("seguinte"));

  const [erro, setErro] = useState<string | null>(null);
  const [aEntrar, setAEntrar] = useState(false);

  async function entrar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErro(null);
    setAEntrar(true);

    const dados = new FormData(event.currentTarget);

    try {
      await signIn(
        String(dados.get("email") ?? "").trim(),
        String(dados.get("password") ?? ""),
      );
      // Drop the cached "not signed in" answer so the session resolves again.
      queryClient.clear();
      navigate(destino, { replace: true });
    } catch (error) {
      setErro(
        error instanceof ApiError ? error.message : "Não foi possível entrar. Tenta novamente.",
      );
      setAEntrar(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 py-20 text-center">
      <p className="font-display text-lg italic text-ouro-600">Era uma vez…</p>
      <h1 className="mt-2 font-display text-4xl text-tinta">Bem-vindos de volta</h1>
      <p className="mt-3 text-sm text-tinta-700">
        Um cantinho só do {couple.nameOne} &amp; da {couple.nameTwo}.
      </p>

      <div className="my-7">
        <Flourish />
      </div>

      <form onSubmit={entrar} className="w-full space-y-4 text-left">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta-700">Email</span>
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="o-teu@email.pt"
            className="rounded-2xl border border-ouro/25 bg-creme-50 px-4 py-3 text-tinta outline-none transition-colors focus:border-ouro focus:ring-2 focus:ring-ouro/30"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta-700">Palavra-passe</span>
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className="rounded-2xl border border-ouro/25 bg-creme-50 px-4 py-3 text-tinta outline-none transition-colors focus:border-ouro focus:ring-2 focus:ring-ouro/30"
          />
        </label>

        {erro && (
          <p
            role="alert"
            className="rounded-2xl border border-blush/60 bg-blush-300/30 px-4 py-2.5 text-sm text-tinta"
          >
            {erro}
          </p>
        )}

        <button
          type="submit"
          disabled={aEntrar}
          className="w-full rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {aEntrar ? "A entrar…" : "Entrar"}
        </button>
      </form>

      <p className="mt-10 text-xs text-tinta-700/60">
        Espaço privado. Apenas para nós os dois.
      </p>
    </main>
  );
}
